import { Component, OnInit, OnDestroy } from '@angular/core';
import {Router, ResolveStart, ResolveEnd, ActivatedRoute} from '@angular/router';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { environment } from '../../../../environments/environment';

import { errorFormAnimation } from '../../../shared/animations/form-error';
import { Store } from '@ngrx/store';
import { AppState } from '../../../app.state';
import {CUSTOMER_PORTAL_CONFIG, WELCOME_PAGE} from '../../../../constants/local-storage-variables';
import { LoginAction } from 'src/app/core/actions/user/user.actions';
import {
  getAuthenticationError,
  isAuthenticated,
  getAuthenticationLoading
} from '../../../core/selectors/user.selectors';
import { takeUntil, skip, tap, filter } from 'rxjs/operators';
import { Subject, Observable } from 'rxjs';
import { NotificationService } from 'src/app/core/services/notification/notification.service';
import { getIsAnimationVisible } from 'src/app/core/selectors/animations.selector';

import {
  ShowLoadAnimation,
  HideLoadAnimation
} from 'src/app/core/actions/animations/animations.actions';
import {EnvironmentResolveService} from '../../../core/services/environment-resolve/environment-resolve.service';
import {SHOW_REQUEST_ACCESS_PAGE} from '../../../../constants/configuration-variables';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-login-page',
  templateUrl: './login-page.component.html',
  styleUrls: ['./login-page.component.scss'],
  animations: [errorFormAnimation()]
})
export class LoginPageComponent implements OnInit, OnDestroy {
  public appVersion = environment.appVersion;
  public authMethod = this.erService.environmentERS.authMethod || '';
  public readonly niasUrl = this.erService.environmentERS.niasUrl || '/../nias/index.php';
  public readonly showRequestAccessPage: boolean = !!this.erService.environmentERS.showRequestAccessPage;
  public authForm: FormGroup;
  public authError = false;
  public isServerError = false;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  public authLoading$: Observable<boolean>;
  public showAnimation$: Observable<boolean>;
  public authToken: string;

  public get login(): FormControl {
    return this.authForm.get('login') as FormControl;
  }

  public get password(): FormControl {
    return this.authForm.get('password') as FormControl;
  }

  public get keepSignedIn(): FormControl {
    return this.authForm.get('keepSignedIn') as FormControl;
  }

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private notificationService: NotificationService,
    private store: Store<AppState>,
    private activatedRoute: ActivatedRoute,
    private erService: EnvironmentResolveService,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.activatedRoute.params.subscribe(params => {
      this.authToken = params.auth;
      if (this.authToken) {
        this.signinToken();
      }
    });

    this.activatedRoute.queryParams.subscribe(params => {
      const classicLogin = params['classic-login'];
      if (classicLogin) {
        console.log('classic-login enabled:', classicLogin);
        this.authMethod = '';
      }
    });

    this.router.events.pipe(takeUntil(this.destroy$)).subscribe(event => {
      if (event instanceof ResolveStart) {
        this.store.dispatch(ShowLoadAnimation());
      }
      if (event instanceof ResolveEnd) {
        this.store.dispatch(HideLoadAnimation());
      }
    });
    this.showAnimation$ = this.store.select(getIsAnimationVisible);
    this.authForm = this.fb.group({
      login: ['', Validators.required],
      password: ['', [Validators.required, Validators.minLength(3)]],
      keepSignedIn: [false]
    });

    this.store.select(isAuthenticated).subscribe((data) => {

      const returnUrl = localStorage.getItem('returnUrl');
      if (returnUrl) {
        localStorage.setItem('returnUrl', '');
        this.router.navigate([returnUrl]);
      } else {
        if (localStorage.getItem(WELCOME_PAGE)) {
          this.router.navigate([localStorage.getItem(WELCOME_PAGE)]);
        } else {
          this.router.navigate(['/dashboard']);
        }
      }
    });

    this.authLoading$ = this.store.select(getAuthenticationLoading);

    this.store
      .select(getAuthenticationError)
      .pipe(
        takeUntil(this.destroy$),
        filter(error => Boolean(error))
      )
      .subscribe(({ error, message }) => {
        this.isServerError = true;
        this.notificationService.showErrorNotification(error ? error.errorMessage : (message ? message : this.translateService.instant('invalid credentials') ));
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  public signin(): void {
    if(this.checkValidity()) {
      this.store.dispatch(
        LoginAction({
          username: this.login.value,
          password: this.password.value,
          keepSignedIn: this.keepSignedIn.value
        })
      );
    }
  }

  public signinToken(): void {
    this.authToken = atob(this.authToken);
    this.store.dispatch(
      LoginAction({
        username: this.authToken.split(':')[0],
        password: this.authToken.split(':')[1],
        keepSignedIn: true
      })
    );
  }

  public signinNias(): void {
    window.location.href = this.niasUrl;
  }

  private checkValidity(): boolean {
    if(!this.authForm.get('login').errors && !this.authForm.get('password').errors) {
      return true;
    }
    console.log('INVALID LOGIN INFO');
    this.authForm.markAllAsTouched();
    return false;
  }
}
